/**
 * Quick Start Tour
 * Guided walkthrough of bill tracking features
 *
 * Note: This is a non-interactive tour. Driver.js captures focus and closes
 * on outside clicks, so we show features without requiring user interaction.
 * Users can explore and take actions after the tour completes.
 *
 * Navigation: Step 0 runs on the current page, then navigates to /bills for
 * subsequent steps using the tour resume mechanism.
 */

import type { Tour } from '@/types/onboarding';
import { saveTourResumeState } from './committee-tour';

const PERSISTENT_HIGHLIGHT_CLASS = 'tour-persistent-highlight';

/**
 * Add persistent highlight to Bills nav item as a visual anchor
 */
function addBillsNavHighlight() {
  const billsNav = document.querySelector('[data-tour="nav-bills"]');
  billsNav?.classList.add(PERSISTENT_HIGHLIGHT_CLASS);
}

/**
 * Remove persistent highlight when tour ends.
 * Exported so it can be called on tour completion/skip.
 */
export function cleanupQuickStartTour() {
  const billsNav = document.querySelector('[data-tour="nav-bills"]');
  billsNav?.classList.remove(PERSISTENT_HIGHLIGHT_CLASS);
}

/**
 * Navigate to bills page after welcome step.
 * Saves tour state so it can resume at step 1 after navigation.
 */
function navigateToBillsPage(
  _element: Element | undefined,
  _step: unknown,
  { driver }: { driver: { destroy: () => void } }
) {
  // If already on /bills, just continue to next step
  if (window.location.pathname === '/bills') {
    return;
  }

  // Save state to resume at step 1 after navigation
  saveTourResumeState('quick-start', 1);

  // Stop the current driver instance before navigation
  driver.destroy();

  // Navigate to bills page
  window.location.href = '/bills';
}

export const quickStartTour: Tour = {
  id: 'quick-start',
  name: 'Track Your First Bill',
  description: 'Learn how to find and track legislation',
  // No startRoute - step 0 runs on current page, then navigates to /bills
  steps: [
    {
      // Step 0: Welcome to BillBee (runs on current page)
      element: '[data-tour="nav-bills"]',
      popover: {
        title: 'Welcome to BillBee! 🐝',
        description:
          "Your AI-powered legislative intelligence platform. We'll help you track bills, understand voting patterns, and stay informed on what matters to you. Let's head to the Bills page — your home base for tracking legislation.",
        side: 'bottom',
        align: 'start',
        onNextClick: navigateToBillsPage,
      },
      onHighlightStarted: addBillsNavHighlight,
    },
    {
      // Step 1: Bills page overview (on /bills)
      popover: {
        title: 'Your legislative dashboard',
        description:
          "This is where you'll find all current legislation. Let's take a quick look at how to find and track the bills you care about.",
      },
    },
    {
      // Step 2: Search
      element: '[data-tour="bills-search"]',
      popover: {
        title: 'Search for bills',
        description:
          'Use keywords like "education" or "taxes", or enter a specific bill number like "HB 2001".',
        side: 'bottom',
        align: 'start',
      },
    },
    {
      // Step 3: Filters
      element: '[data-tour="bills-filters"]',
      popover: {
        title: 'Filter results',
        description:
          'Narrow your search by chamber, status, or policy category to find exactly what you need.',
        side: 'bottom',
        align: 'start',
      },
    },
    {
      // Step 4: Bill card
      element: '[data-tour="bill-card"]',
      popover: {
        title: 'Bill overview',
        description:
          'Each card shows the bill number, title, current status, and recent activity. Click any card to see full details.',
        side: 'bottom',
        align: 'start',
      },
    },
    {
      // Step 5: Track button
      element: '[data-tour="track-bill-button"]',
      popover: {
        title: 'Track bills you care about',
        description:
          "Click the star icon on any bill to add it to your tracking list. You'll receive updates when tracked bills move forward.",
        side: 'bottom',
        align: 'center',
      },
    },
    {
      // Step 6: My Bills nav
      element: '[data-tour="nav-my-bills"]',
      popover: {
        title: 'Your tracked bills',
        description:
          'All your tracked legislation appears in the My Bills page. Check back anytime to see status updates.',
        side: 'bottom',
        align: 'start',
      },
    },
    {
      // Step 7: Completion
      popover: {
        title: "You're ready to go!",
        description:
          "Start by tracking a bill that interests you. You'll find more tips in the Setup Guide.",
      },
      onDeselected: cleanupQuickStartTour,
    },
  ],
};
